import { api } from './api';

// Permissões e funções (roles) do SAMASC. Rotas só para administradores
// (`user_adm`), em /access-control na API.

export type AccessControlKind = 'permission' | 'role';

export interface AccessControlItem {
  id: string;
  name: string;
  identification: string;
  description: string | null;
  created_at: string;
  active_accesses: number;
  total_accesses: number;
  bi_reports: number;
}

export interface AccessControlUser {
  id: string;
  name: string;
  nickname: string;
  email: string | null;
  status: boolean;
  sector: string | null;
}

export interface AccessControlItemInput {
  name: string;
  identification: string;
  description: string;
}

export interface AccessLinks {
  permissions_ids: string[];
  roles_ids: string[];
}

export interface SyncResult {
  added: number;
  removed: number;
}

export function accessControlError(err: unknown, fallback: string): string {
  const message = (err as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message;

  return typeof message === 'string' && message.trim() ? message : fallback;
}

const path = (kind: AccessControlKind) =>
  kind === 'permission' ? 'permissions' : 'roles';

export const accessControlService = {
  async listItems(kind: AccessControlKind): Promise<AccessControlItem[]> {
    const response = await api.get<AccessControlItem[]>(
      `/access-control/${path(kind)}`,
    );

    return Array.isArray(response.data) ? response.data : [];
  },

  async createItem(
    kind: AccessControlKind,
    data: AccessControlItemInput & { accesses_ids: string[] },
  ): Promise<AccessControlItem> {
    const response = await api.post<AccessControlItem>(
      `/access-control/${path(kind)}`,
      data,
    );

    return response.data;
  },

  async updateItem(
    kind: AccessControlKind,
    id: string,
    data: Partial<AccessControlItemInput>,
  ): Promise<AccessControlItem> {
    const response = await api.put<AccessControlItem>(
      `/access-control/${path(kind)}/${id}`,
      data,
    );

    return response.data;
  },

  async deleteItem(kind: AccessControlKind, id: string): Promise<void> {
    await api.delete(`/access-control/${path(kind)}/${id}`);
  },

  async listUsers(): Promise<AccessControlUser[]> {
    const response = await api.get<AccessControlUser[]>(
      '/access-control/users',
    );

    return Array.isArray(response.data) ? response.data : [];
  },

  async listItemUsers(
    kind: AccessControlKind,
    id: string,
  ): Promise<AccessControlUser[]> {
    const response = await api.get<AccessControlUser[]>(
      `/access-control/${path(kind)}/${id}/users`,
    );

    return Array.isArray(response.data) ? response.data : [];
  },

  async syncItemUsers(
    kind: AccessControlKind,
    id: string,
    accessesIds: string[],
  ): Promise<SyncResult> {
    const response = await api.put<SyncResult>(
      `/access-control/${path(kind)}/${id}/users`,
      { accesses_ids: accessesIds },
    );

    return response.data;
  },

  async getAccessLinks(accessId: string): Promise<AccessLinks> {
    const response = await api.get<AccessLinks>(
      `/access-control/users/${accessId}/links`,
    );

    return response.data;
  },

  async syncAccessLinks(
    accessId: string,
    links: Partial<AccessLinks>,
  ): Promise<void> {
    await api.put(`/access-control/users/${accessId}/links`, links);
  },
};
